import styles from "./MasterScheduleEventButtonModal.module.css";
import {
    Button, ButtonProps,
    Checkbox,
    Col,
    DatePicker,
    Form,
    Input,
    Modal,
    notification,
    Row,
    Select, Space,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import { InputDateTimeFormat, MasterScheduleEvent, ProductionLine } from "../../api/models";
import React, { useContext, useEffect, useState } from "react";
import { ConfiguratorContext } from "../../context";
import { useIntl } from "react-intl";
import Utils from "../../util/util";
import {FormProps, useWatch} from "antd/es/form/Form";
import BMButton from "../../components/BMButton";
import {ValidateErrorEntity} from "rc-field-form/lib/interface";

interface MasterScheduleEventFormValue {
    id?: number
    productionLineSet?: string[]
    name?: string
    startDate?: Dayjs
    endDate?: Dayjs
    isDefault?: boolean
    bevTaktRate?: number;
    iceTaktRate?: number;
}

const MasterScheduleEventButtonModal = (props: Omit<ButtonProps, 'onChange'> & {
    startDate: Dayjs | undefined
    event?: MasterScheduleEvent
    onChange?: (e?:MasterScheduleEvent | undefined) => void
}) => {

    const { startDate: a, event: c, onChange:d, ...btnProps } = props;

    const configurator = useContext(ConfiguratorContext);
    const intl = useIntl();
    const [open, setOpen] = useState(false);
    const [form] = Form.useForm();
    const [initialValues, setInitialValues] = useState<MasterScheduleEventFormValue>();
    const isHoliday = useWatch('isHoliday', form);
    const selectedLinesOnEvent = useWatch('productionLineSet', form);
    const selectedEventId = initialValues?.id;
    const isNew = !selectedEventId;

    useEffect(() => {
        if (isHoliday) {
            form.setFieldValue('iceTaktRate', 0 );
            form.setFieldValue('bevTaktRate', 0 );
        }
    }, [isHoliday]);

    useEffect(() => {
        form.resetFields();
    }, [initialValues]);

    const createMasterScheduleEvent = async (evt:MasterScheduleEvent): Promise<MasterScheduleEvent | undefined>  => {

        try {
            const saved = await configurator.api.createMasterScheduleEvent(evt);
            return saved.data;
        }
        catch(e: any) {
            const errorMsg = intl.formatMessage({ id: e.response?.data?.message || e.message });
            notification.error( { message: "Failed to create master schedule slot. " + errorMsg });
        }
    }

    const updateMasterScheduleEvent = async (evt:MasterScheduleEvent) : Promise<MasterScheduleEvent | undefined> =>  {

        try {
            const saved = await configurator.api.updateMasterScheduleEvent(evt);
            return saved.data;
        }
        catch(e: any) {
            const errorMsg = intl.formatMessage({ id: e.response?.data?.message || e.message });
            notification.error( { message: "Failed to update master schedule slot. " + errorMsg });
        }
    }

    const handleDeleteMasterScheduleEvent = async () => {
        const eventId = selectedEventId;
        if (!eventId) return;

        const deletedId = await deleteMasterScheduleEvent(eventId);
        if ( deletedId ) {
            notification.success( { message: "Event deleted. " });

            props.onChange?.()
            setOpen(false);
        }
    }

    const deleteMasterScheduleEvent = async (eventId:number) => {
        try {
            await configurator.api.deleteMasterScheduleEvent(eventId);
            return eventId;
        }
        catch(e: any) {
            const errorMsg = intl.formatMessage({ id: e.response?.data?.message || e.message });
            notification.error( { message: "Failed to delete master schedule slot. " + errorMsg });
        }

        return;
    }

    const handleOpen = () => {

        const initialValues = props.event;
        if (initialValues) {
            setInitialValues({
                ...initialValues,
                startDate: dayjs(initialValues.startDate),
                endDate: dayjs(initialValues.endDate),
            });
        }
        else {
            setInitialValues({
                startDate: props.startDate,
                productionLineSet: props.event?.productionLineSet,
            });
        }
    };

    const handleCopy = () => {
        setInitialValues({
            ...initialValues,
            id: undefined,
            name: undefined,
            isDefault: false,
            startDate: props.startDate,
            endDate: props.startDate,
            productionLineSet: props.event?.productionLineSet,
        })
    }

    const handleProductionLineOk = async () => {
        try {
            const values = await form.validateFields();

            const startDate = values.startDate.format(InputDateTimeFormat);
            const endDate = values.endDate?.format(InputDateTimeFormat) || form.getFieldValue("startDate").add(1, 'day').format(InputDateTimeFormat);

            const event = {
                ...values,
                startDate,
                endDate
            }

            const saved = isNew
                ? await createMasterScheduleEvent(event)
                : await updateMasterScheduleEvent(event);

            if (saved) {
                const message = isNew ? "Event added." : "Event updated. ";
                notification.success({message});

                setOpen(false);
                props.onChange?.(saved);
            }
        }
        catch(e: any) {
            const validationErrors = e as ValidateErrorEntity;
            const errs = validationErrors.errorFields.map( f => f.errors ).flat().map( (msg, ndx) => <li key={`validationError-${ndx}`}>{msg}</li> );
            const errorMsg = !errs.length ? "Please fix validation errors." : <ul>{errs}</ul>;
            notification.error({message: errorMsg });
        }
    }

    const handleCancel = () => {
        setOpen(false);
    }

    return (
        <div className={styles["master-schedule-event-modal"]}>
            <BMButton type="text"
                      onClick={() => setOpen(true)}
                      {...btnProps} />
            <Modal
                title={<div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginRight: "2rem"}}>
                    <div>{selectedEventId ? "Edit" : "Add"} Event for Takt Rate</div>
                    {!isNew &&
                    <Button type="primary" onClick={handleCopy}>Copy</Button>}
                </div>}
                open={open}
                onClose={handleCancel}
                onCancel={handleCancel}
                afterOpenChange={(open) => {
                    if( open) {
                        handleOpen();
                    }
                }}
                footer={
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                        {!isNew
                            ? <Button key="delete" type="primary" danger onClick={handleDeleteMasterScheduleEvent}>
                                Delete Event
                            </Button>
                            :<div></div>}

                        <Space>
                            <Button onClick={handleCancel} >
                                Cancel
                            </Button>
                            <Button type="primary" onClick={handleProductionLineOk} >
                                Save
                            </Button>
                        </Space>
                    </div>
                }
            >

                <div className={styles["carousel-slide"]}>
                        <SharedForm
                            form={form}
                            initialValues={initialValues}
                            selectedLineOnEvent={selectedLinesOnEvent}
                        />
                </div>

            </Modal>

        </div>
    );
}

const SharedForm = (props: FormProps & {
    selectedLineOnEvent?: string[]
}) => {

    const productionLines= Object.values(ProductionLine);

    const { selectedLineOnEvent, ...formProps } = props;
    return <Form {...formProps} layout="vertical">
        <Form.Item name="id" hidden={true} >
            <Input />
        </Form.Item>
        <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: "Please input name" }]} >
            <Input placeholder="Enter event name" />
        </Form.Item>
        <Form.Item
            label="Production Line"
            name="productionLineSet"
            rules={[{ required: true, message: "Please select production line" }]}
        >
            <Select
                placeholder="Select production line"
                value={selectedLineOnEvent}
                options={productionLines.map((line) => ({
                    key: line,
                    value: line,
                    label: Utils.snakeCaseToFirstLetterCapitalized(line.split("_").slice(1).join(" ")),
                }))}
                mode="multiple"
                allowClear />
        </Form.Item>
        <Row justify={"space-between"}>
            <Form.Item
                label="ICE Takt Rate"
                name="iceTaktRate"
                rules={[{ required: true, message: "Please input takt rate" }]}
            >
                <Input
                    type="number"
                    step="0.5"
                    placeholder="Enter takt rate"
                    style={{ width: "200px" }} />
            </Form.Item>
            <Form.Item
                label="BEV Takt Rate"
                name="bevTaktRate"
                rules={[{ required: true, message: "Please input takt rate" }]}
            >
                <Input
                    type="number"
                    step="0.5"
                    placeholder="Enter takt rate"
                    style={{ width: "200px" }} />
            </Form.Item>
        </Row>
        <Row justify={"space-between"}>
            <Form.Item name="startDate" label="Start Date">
                <DatePicker style={{ width: "200px" }} />
            </Form.Item>
            <Form.Item name="endDate" label="End Date">
                <DatePicker style={{ width: "200px" }} />
            </Form.Item>
        </Row>
        <Row>
            <Col span={14}>
                <Form.Item name="isDefault" label="Default" valuePropName="checked" initialValue={false}>
                    <Checkbox />
                </Form.Item>
            </Col>
            <Col span={10}>
                <Form.Item name="isHoliday" label="Holiday" valuePropName="checked" initialValue={false}>
                    <Checkbox />
                </Form.Item>
            </Col>
        </Row>
    </Form>;
}

export default MasterScheduleEventButtonModal;